// This is the list of languages that are supported by the Coval voice system right now.
export const SUPPORTED_LANGUAGES = [
  "bg-BG",
  "bn-IN",
  "da-DK",
  "en-AU",
  "en-IN",
  "en-GB",
  "en-US",
  "fr-CA",
  "fr-FR",
  "de-DE",
  "hi-IN",
  "ja-JP",
  "ko-KR",
  "lt-LT",
  "pl-PL",
  "es-ES",
  "es-US",
  "pt-BR",
  "zh-CN",
  "zh-HK",
  "en",
  "de",
  "es",
  "hi",
  "ja",
  "ko",
  "ar",
] as const

// Should be a subset of SUPPORTED_LANGUAGES.
const GOOGLE_SUPPORTED_LANGUAGES = [
  "bg-BG",
  "bn-IN",
  "da-DK",
  "en-AU",
  "en-IN",
  "en-GB",
  "en-US",
  "fr-CA",
  "fr-FR",
  "de-DE",
  "hi-IN",
  "ja-JP",
  "lt-LT",
  "pl-PL",
  "es-ES",
  "es-US",
  "pt-BR",
  "zh-CN",
  "zh-HK",
] as const

const GRADIUM_PROVIDER = "gradium" as const
const EN_US_LANGUAGES = ["en", "en-US"] as const
const EN_GB_LANGUAGES = ["en", "en-GB"] as const
const EN_REGIONAL_LANGUAGES = [
  "en",
  "en-US",
  "en-GB",
  "en-AU",
  "en-IN",
] as const

export interface CovalVoice {
  name: string // Lowercase Coval name (API format)
  displayName: string // Title case for UI display
  voicePresentation: "Masculine" | "Feminine" // Perceived vocal presentation
  accentLabel?: string // Accent/nationality shown in supporting UI metadata
  languages?: readonly string[] // List of languages supported by the voice
  pitch?: "low" | "medium" | "high" // Human-audited pitch bucket
  secondaryQualities?: readonly string[] // Future perceptual tags
  // Set to false for voices whose runtime backend has no speed control.
  // Omitted means the voice supports speed adjustment.
  supportsSpeed?: false
  // Set to false while the voice has no uploaded preview clip.
  // Omitted means a preview is available at the deterministic S3 path.
  hasPreview?: false
  // Third-party provider metadata (if applicable)
  provider?:
    | "cartesia"
    | "elevenlabs"
    | "openai"
    | "polly"
    | "playht"
    | "hume"
    | "rime"
    | "gradium"
  voiceId?: string
}

// Preview audio for every voice lives at a deterministic S3 path. Adding a
// new voice to COVAL_VOICES is therefore not enough on its own — you must
// also generate and upload the matching mp3. Run:
//
//   cd backend && python scripts/generate_voice_previews.py --missing
//
// CI verifies every voice's preview is reachable; see the .test.ts file.
// Per-variant preview clips (one per accent/language), generated by the same
// script with `voice.languages` looped in full rather than just the first
// entry. Lives at a separate prefix from the legacy single-clip layout above.
const S3_VOICE_BASE_V2 =
  "https://coval-voice-previews.s3.us-east-2.amazonaws.com/voice-previews-v2"

// Explicit mapping of which S3_VOICE_BASE_V2 file plays for a given voice +
// language/accent code. Every key is a code the voice can actually be
// selected with; the value is the exact variant filename (no extension,
// matches what `generate_voice_previews.py` uploaded) that plays for it.
//
// Multiple keys can point at the same value on purpose — e.g. a voice whose
// provider can't produce different audio per region has every one of its
// accent codes point at the one file that actually exists. This is a direct,
// literal mapping: no derivation, no fallback matching, no slug-mangling —
// what you see here is exactly what plays.
//
// Every voice/code the picker can offer has an entry. The consistency test
// checks this table against `COVAL_VOICES`, so a gap here is a stale table
// rather than a runtime case to handle.
export const VOICE_PREVIEWS: Record<string, Record<string, string>> = {
  aria: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  ashwin: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  autumn: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  brynn: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  callum: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  caspian: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  corwin: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  darrow: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  delphine: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  dorian: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  elara: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  kieran: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  lysander: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  marina: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  naveen: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  orion: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  rowan: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  skye: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  soren: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  vera: {
    "bg-BG": "bg-BG",
    "bn-IN": "bn-IN",
    "da-DK": "da-DK",
    "de-DE": "de-DE",
    "en-AU": "en-AU",
    "en-GB": "en-GB",
    "en-IN": "en-IN",
    "en-US": "en-US",
    "es-ES": "es-ES",
    "es-US": "es-US",
    "fr-CA": "fr-CA",
    "fr-FR": "fr-FR",
    "hi-IN": "hi-IN",
    "ja-JP": "ja-JP",
    "lt-LT": "lt-LT",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    "zh-CN": "zh-CN",
    "zh-HK": "zh-HK",
  },
  alejandro: {
    en: "en",
    es: "es",
  },
  erika: {
    es: "es",
    en: "en",
  },
  monika: {
    en: "en",
    hi: "hi",
  },
  mark: {
    en: "en",
    de: "de",
    es: "es",
  },
  angela: {
    en: "en",
    es: "es",
  },
  raju: {
    en: "en",
  },
  isabella: {
    es: "es",
  },
  harry: {
    ko: "ko",
    "ko-KR": "ko",
    hi: "hi",
    "hi-IN": "hi",
    en: "en",
    "en-AU": "en",
    "en-IN": "en",
    "en-GB": "en",
    "en-US": "en",
    es: "es",
    "es-ES": "es",
    "es-US": "es",
    "pl-PL": "pl",
    ar: "ar",
  },
  amir: {
    ar: "ar",
    en: "en",
  },
  layla: {
    ar: "ar",
    en: "en",
  },
  noa: {
    en: "en",
    "en-GB": "en",
  },
  yossi: {
    en: "en",
    "en-US": "en",
  },
  kehinde: {
    en: "en",
  },
  victor: {
    en: "en",
  },
  luis: {
    en: "en",
    "en-US": "en",
  },
  lisa: {
    en: "en",
    "en-US": "en",
  },
  leo: {
    en: "en",
    "en-US": "en",
  },
  martin: {
    en: "en",
    "en-US": "en",
  },
  marshal: {
    en: "en",
    "en-GB": "en",
  },
  raven: {
    en: "en",
    "en-GB": "en",
  },
  alexei: {
    en: "en",
  },
  puthina: {
    en: "en",
  },
  darryl: {
    en: "en",
  },
  juvy: {
    en: "en",
  },
  pedro: {
    en: "en",
  },
  rachel: {
    en: "en",
  },
  burak: {
    en: "en",
    "en-GB": "en",
  },
  walker: {
    en: "en",
    "en-US": "en",
  },
  cletus: {
    en: "en",
    "en-US": "en",
  },
  bubba: {
    en: "en",
    "en-US": "en",
  },
  cowboy: {
    en: "en",
    "en-US": "en",
  },
  sallymay: {
    en: "en",
    "en-US": "en",
  },
  jay: {
    en: "en",
    "en-GB": "en",
  },
  ziyu: {
    en: "en",
    "en-GB": "en",
  },
  isla: {
    en: "en",
    "en-GB": "en",
  },
  chris: {
    en: "en",
    "en-GB": "en",
  },
  alok: {
    en: "en",
    "en-IN": "en",
  },
  mani: {
    en: "en",
    "en-IN": "en",
  },
  vidya: {
    en: "en",
    "en-IN": "en",
  },
  hanna: {
    en: "en",
    "en-US": "en",
  },
  emma: {
    en: "en",
    "en-US": "en",
  },
  kent: {
    en: "en",
    "en-US": "en",
  },
  sydney: {
    en: "en",
    "en-US": "en",
  },
  john: {
    en: "en",
    "en-US": "en",
  },
  eva: {
    en: "en",
    "en-GB": "en",
  },
  jack: {
    en: "en",
    "en-GB": "en",
  },
  zoey: {
    en: "en",
    "en-US": "en",
  },
  harper: {
    en: "en",
    "en-US": "en",
  },
  riley: {
    en: "en",
    "en-US": "en",
  },
  skyler: {
    en: "en",
    "en-US": "en",
  },
  quinn: {
    en: "en",
    "en-US": "en",
  },
  lukas: {
    en: "en",
    "en-US": "en",
    "en-GB": "en",
    "en-AU": "en",
    "en-IN": "en",
  },
}

/**
 * Voices synthesized by the higher-realism stack. They are the catalog's most
 * natural-sounding voices, and the same stack caps them at roughly 12
 * simultaneous connections.
 */
const HIGH_NATURALNESS_VOICE_NAMES = new Set([
  "alejandro",
  "alok",
  "amir",
  "angela",
  "bubba",
  "burak",
  "chris",
  "cletus",
  "cowboy",
  "darryl",
  "erika",
  "hanna",
  "harry",
  "isla",
  "jay",
  "juvy",
  "kehinde",
  "layla",
  "leo",
  "lisa",
  "luis",
  "mani",
  "mark",
  "marshal",
  "martin",
  "monika",
  "noa",
  "pedro",
  "puthina",
  "rachel",
  "raju",
  "raven",
  "sallymay",
  "victor",
  "vidya",
  "walker",
  "yossi",
  "ziyu",
])

/** Which synthesis stack a voice runs, and so how human it sounds. */
export type VoiceNaturalness = "high" | "standard"

export const getVoiceNaturalness = (voiceName?: string): VoiceNaturalness =>
  voiceName && HIGH_NATURALNESS_VOICE_NAMES.has(voiceName) ? "high" : "standard"

export function getVoicePreviewUrl(
  voiceName: string,
  languageCode?: string,
  previewsByVoice: Record<string, Record<string, string>> = VOICE_PREVIEWS,
): string {
  const normalizedVoiceName = voiceName.trim()
  if (!normalizedVoiceName) {
    throw new Error("voiceName is required")
  }

  // A voice/code with no entry falls back to the voice's first declared code,
  // which stands in as a generic sample of how the voice sounds.
  const previewsForVoice = previewsByVoice[normalizedVoiceName] ?? {}
  const variant =
    (languageCode ? previewsForVoice[languageCode] : undefined) ??
    Object.values(previewsForVoice)[0] ??
    languageCode ??
    ""

  return `${S3_VOICE_BASE_V2}/${encodeURIComponent(normalizedVoiceName)}/${encodeURIComponent(variant)}.mp3`
}

export const COVAL_VOICES: CovalVoice[] = [
  {
    name: "aria",
    displayName: "Aria",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "ashwin",
    displayName: "Ashwin",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "high",
  },
  {
    name: "autumn",
    displayName: "Autumn",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "brynn",
    displayName: "Brynn",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "callum",
    displayName: "Callum",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "high",
  },
  {
    name: "caspian",
    displayName: "Caspian",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "low",
  },
  {
    name: "corwin",
    displayName: "Corwin",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "low",
  },
  {
    name: "darrow",
    displayName: "Darrow",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "low",
  },
  {
    name: "delphine",
    displayName: "Delphine",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "dorian",
    displayName: "Dorian",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "high",
  },
  {
    name: "elara",
    displayName: "Elara",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "kieran",
    displayName: "Kieran",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "lysander",
    displayName: "Lysander",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "marina",
    displayName: "Marina",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "naveen",
    displayName: "Naveen",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "orion",
    displayName: "Orion",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "rowan",
    displayName: "Rowan",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "emma",
    displayName: "Emma",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "low",
    provider: GRADIUM_PROVIDER,
    voiceId: "YTpq7expH9539ERJ",
  },
  {
    name: "kent",
    displayName: "Kent",
    voicePresentation: "Masculine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "medium",
    provider: GRADIUM_PROVIDER,
    voiceId: "LFZvm12tW_z0xfGo",
  },
  {
    name: "sydney",
    displayName: "Sydney",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "medium",
    provider: GRADIUM_PROVIDER,
    voiceId: "jtEKaLYNn6iif5PR",
  },
  {
    name: "john",
    displayName: "John",
    voicePresentation: "Masculine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "low",
    provider: GRADIUM_PROVIDER,
    voiceId: "KWJiFWu2O9nMPYcR",
  },
  {
    name: "eva",
    displayName: "Eva",
    voicePresentation: "Feminine",
    accentLabel: "British",
    languages: EN_GB_LANGUAGES,
    pitch: "medium",
    provider: GRADIUM_PROVIDER,
    voiceId: "ubuXFxVQwVYnZQhy",
  },
  {
    name: "jack",
    displayName: "Jack",
    voicePresentation: "Masculine",
    accentLabel: "British",
    languages: EN_GB_LANGUAGES,
    pitch: "medium",
    provider: GRADIUM_PROVIDER,
    voiceId: "m86j6D7UZpGzHsNu",
  },
  {
    name: "zoey",
    displayName: "Zoey",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "high",
    secondaryQualities: ["playful", "upbeat"],
    provider: GRADIUM_PROVIDER,
    voiceId: "NbpkqMVS3CJeq2j8",
  },
  {
    name: "harper",
    displayName: "Harper",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "medium",
    secondaryQualities: ["warm", "confident"],
    provider: GRADIUM_PROVIDER,
    voiceId: "4SZHfMpw-p46Ywgs",
  },
  {
    name: "riley",
    displayName: "Riley",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "high",
    secondaryQualities: ["bright", "approachable"],
    provider: GRADIUM_PROVIDER,
    voiceId: "7aEKz4P1ogZ0UsRP",
  },
  {
    name: "skyler",
    displayName: "Skyler",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "high",
    secondaryQualities: ["breezy", "wistful"],
    provider: GRADIUM_PROVIDER,
    voiceId: "cLONiZ4hQ8VpQ4Sz",
  },
  {
    name: "quinn",
    displayName: "Quinn",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "medium",
    secondaryQualities: ["snappy", "contemporary"],
    provider: GRADIUM_PROVIDER,
    voiceId: "vtG8ddh4IN32Otad",
  },
  {
    name: "lukas",
    displayName: "Lukas (Eastern European)",
    voicePresentation: "Masculine",
    languages: EN_REGIONAL_LANGUAGES,
    pitch: "medium",
    provider: GRADIUM_PROVIDER,
    voiceId: "_di_fkYdBWrbSGIL",
  },
  {
    name: "alba",
    displayName: "Alba",
    voicePresentation: "Masculine",
    languages: ["en"],
    pitch: "low",
    supportsSpeed: false,
    hasPreview: false,
  },
  {
    name: "soren",
    displayName: "Soren",
    voicePresentation: "Masculine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "skye",
    displayName: "Skye",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "medium",
  },
  {
    name: "vera",
    displayName: "Vera",
    voicePresentation: "Feminine",
    // This voice supports all languages
    languages: GOOGLE_SUPPORTED_LANGUAGES,
    pitch: "low",
  },
  {
    name: "alejandro",
    displayName: "Alejandro (Latin America)",
    voicePresentation: "Masculine",
    languages: ["en", "es"],
    pitch: "medium",
  },
  {
    name: "angela",
    displayName: "Angela",
    voicePresentation: "Feminine",
    accentLabel: "American",
    languages: ["en", "es"],
    pitch: "low",
  },
  {
    name: "erika",
    displayName: "Erika (Latin America)",
    voicePresentation: "Feminine",
    languages: ["en", "es"],
    pitch: "medium",
    secondaryQualities: ["raspy"],
  },
  {
    name: "harry",
    displayName: "Harry (Multilingual)",
    voicePresentation: "Masculine",
    languages: [
      "ko",
      "ko-KR",
      "en",
      "en-AU",
      "en-IN",
      "en-GB",
      "en-US",
      "hi",
      "hi-IN",
      "es",
      "es-ES",
      "es-US",
      "pl-PL",
      "ar",
    ],
    pitch: "low",
  },
  {
    name: "amir",
    displayName: "Amir (Arabic)",
    voicePresentation: "Masculine",
    languages: ["ar", "en"],
    pitch: "high",
  },
  {
    name: "layla",
    displayName: "Layla (Arabic)",
    voicePresentation: "Feminine",
    languages: ["ar", "en"],
    pitch: "high",
  },
  {
    name: "noa",
    displayName: "Noa",
    voicePresentation: "Feminine",
    accentLabel: "British",
    languages: EN_GB_LANGUAGES,
    pitch: "low",
  },
  {
    name: "yossi",
    displayName: "Yossi",
    voicePresentation: "Masculine",
    accentLabel: "American",
    languages: EN_US_LANGUAGES,
    pitch: "high",
  },
  {
    name: "mark",
    displayName: "Mark",
    voicePresentation: "Masculine",
    accentLabel: "American",
    languages: ["en", "de", "es"],
    pitch: "medium",
  },
  {
    name: "monika",
    displayName: "Monika (Indian)",
    voicePresentation: "Feminine",
    languages: ["en", "hi"],
    pitch: "high",
  },
  {
    name: "raju",
    displayName: "Raju (Indian)",
    voicePresentation: "Masculine",
    languages: ["en"],
    pitch: "medium",
  },
  {
    name: "isabella",
    displayName: "Isabella (Latin America)",
    voicePresentation: "Feminine",
    // Our Deepgram project is only entitled to aura-2-celeste-es; the -en
    // variant returns 403, so this voice is Spanish-only.
    languages: ["es"],
    pitch: "medium",
    // This voice has no runtime speed control; hide the slider.
    supportsSpeed: false,
  },
  {
    name: "kehinde",
    displayName: "Kehinde (Nigerian)",
    voicePresentation: "Feminine",
    languages: ["en"],
    pitch: "low",
  },
  {
    name: "victor",
    displayName: "Victor (Nigerian)",
    voicePresentation: "Masculine",
    languages: ["en"],
    pitch: "low",
  },
  {
    name: "luis",
    displayName: "Luis (Spanish-Accented)",
    voicePresentation: "Masculine",
    languages: ["en", "en-US"],
    pitch: "medium",
  },
  {
    name: "lisa",
    displayName: "Lisa (Spanish-Accented)",
    voicePresentation: "Feminine",
    languages: ["en", "en-US"],
    pitch: "medium",
    secondaryQualities: ["vocal-fry"],
  },
  {
    name: "leo",
    displayName: "Leo (Spanish-Accented)",
    voicePresentation: "Masculine",
    languages: ["en", "en-US"],
    pitch: "low",
  },
  {
    name: "martin",
    displayName: "Martin (Spanish-Accented)",
    voicePresentation: "Masculine",
    languages: ["en", "en-US"],
    pitch: "low",
  },
  {
    name: "marshal",
    displayName: "Marshal (German-Accented)",
    voicePresentation: "Masculine",
    languages: ["en", "en-GB"],
    pitch: "medium",
  },
  {
    name: "raven",
    displayName: "Raven (German-Accented)",
    voicePresentation: "Feminine",
    languages: ["en", "en-GB"],
    pitch: "medium",
    secondaryQualities: ["gravelly"],
  },
  {
    name: "alexei",
    displayName: "Alexei (Eastern European)",
    voicePresentation: "Masculine",
    languages: ["en"],
    pitch: "medium",
    secondaryQualities: ["russian-accented"],
  },
  {
    name: "puthina",
    displayName: "Puthina (Malay)",
    voicePresentation: "Feminine",
    languages: ["en"],
    pitch: "medium",
  },
  {
    name: "darryl",
    displayName: "Darryl (Malay)",
    voicePresentation: "Masculine",
    languages: ["en"],
    pitch: "high",
  },
  {
    name: "juvy",
    displayName: "Juvy (Filipino)",
    voicePresentation: "Feminine",
    languages: ["en"],
    pitch: "medium",
  },
  {
    name: "pedro",
    displayName: "Pedro (Filipino)",
    voicePresentation: "Masculine",
    languages: ["en"],
    pitch: "high",
  },
  {
    name: "rachel",
    displayName: "Rachel (Filipino)",
    voicePresentation: "Feminine",
    languages: ["en"],
    pitch: "high",
    secondaryQualities: ["vocal-fry"],
  },
  {
    name: "burak",
    displayName: "Burak (Turkish-Accented)",
    voicePresentation: "Masculine",
    languages: ["en", "en-GB"],
    pitch: "low",
  },
  {
    name: "walker",
    displayName: "Walker (US Southern)",
    voicePresentation: "Feminine",
    languages: ["en", "en-US"],
    pitch: "low",
  },
  {
    name: "cletus",
    displayName: "Cletus (US Southern)",
    voicePresentation: "Masculine",
    languages: ["en", "en-US"],
    pitch: "high",
    secondaryQualities: ["raspy"],
  },
  {
    name: "bubba",
    displayName: "Bubba (US Southern)",
    voicePresentation: "Masculine",
    languages: ["en", "en-US"],
    pitch: "medium",
    secondaryQualities: ["gravelly"],
  },
  {
    name: "cowboy",
    displayName: "Cowboy Chris (US Southern)",
    voicePresentation: "Masculine",
    languages: ["en", "en-US"],
    pitch: "low",
    secondaryQualities: ["supportive"],
  },
  {
    name: "sallymay",
    displayName: "Miss Sally May (US Southern)",
    voicePresentation: "Feminine",
    languages: ["en", "en-US"],
    pitch: "medium",
    secondaryQualities: ["warm"],
  },
  {
    name: "jay",
    displayName: "Jay (Chinese-Accented)",
    voicePresentation: "Masculine",
    languages: ["en", "en-GB"],
    pitch: "low",
  },
  {
    name: "ziyu",
    displayName: "Ziyu (Chinese-Accented)",
    voicePresentation: "Feminine",
    languages: ["en", "en-GB"],
    pitch: "high",
  },
  {
    name: "isla",
    displayName: "Isla (Scottish)",
    voicePresentation: "Feminine",
    languages: ["en", "en-GB"],
    pitch: "medium",
  },
  {
    name: "chris",
    displayName: "Chris (Scottish)",
    voicePresentation: "Masculine",
    languages: ["en", "en-GB"],
    pitch: "medium",
  },
  {
    name: "alok",
    displayName: "Alok (Indian)",
    voicePresentation: "Masculine",
    languages: ["en", "en-IN"],
    pitch: "medium",
  },
  {
    name: "mani",
    displayName: "Mani (Indian)",
    voicePresentation: "Masculine",
    languages: ["en", "en-IN"],
    pitch: "low",
  },
  {
    name: "vidya",
    displayName: "Vidya (Indian)",
    voicePresentation: "Feminine",
    languages: ["en", "en-IN"],
    pitch: "medium",
  },
  {
    name: "hanna",
    displayName: "Hanna (Korean-Accented)",
    voicePresentation: "Feminine",
    languages: ["en", "en-US"],
    pitch: "medium",
  },
]

export const DEFAULT_COVAL_VOICE = "aria"
export const DEFAULT_NEW_PERSONA_VOICE = "emma"
export const DEFAULT_LANGUAGE_CODE = "en-US"

// Voice volume + speed slider bounds. Volume is a gain multiplier on the TTS
// audio output; speed is a playback rate multiplier. Some voices expose a
// narrower supported speed range than the API storage envelope.
export const VOICE_VOLUME_MIN = 0
export const VOICE_VOLUME_MAX = 2
export const VOICE_VOLUME_DEFAULT = 1
export const VOICE_VOLUME_STEP = 0.05

export const clampVoiceVolume = (value?: number | null): number | undefined => {
  if (value == null || !Number.isFinite(value)) {
    return undefined
  }

  return Math.min(VOICE_VOLUME_MAX, Math.max(VOICE_VOLUME_MIN, value))
}

const VOICE_SPEED_MIN = 0.25
const VOICE_SPEED_MAX = 2
export const VOICE_SPEED_DEFAULT = 1
export const VOICE_SPEED_STEP = 0.05

const LIMITED_VOICE_SPEED_MIN = 0.7
const LIMITED_VOICE_SPEED_MAX = 1.2

interface VoiceSpeedRange {
  min: number
  max: number
}

export function getVoiceSpeedRange(
  voiceName?: string,
): VoiceSpeedRange | undefined {
  if (!voiceName) {
    return undefined
  }

  const voice = COVAL_VOICES.find((entry) => entry.name === voiceName)
  if (!voice) {
    return {
      min: LIMITED_VOICE_SPEED_MIN,
      max: LIMITED_VOICE_SPEED_MAX,
    }
  }

  if (voice.supportsSpeed === false) {
    return undefined
  }

  // Broad-language voice detection relies on this shared array reference; change
  // this check before spreading/copying GOOGLE_SUPPORTED_LANGUAGES for voices.
  if (voice.languages === GOOGLE_SUPPORTED_LANGUAGES) {
    return { min: VOICE_SPEED_MIN, max: VOICE_SPEED_MAX }
  }

  return {
    min: LIMITED_VOICE_SPEED_MIN,
    max: LIMITED_VOICE_SPEED_MAX,
  }
}

export const clampVoiceSpeed = (
  value?: number | null,
  voiceName?: string,
): number | undefined => {
  if (value == null || !Number.isFinite(value)) {
    return undefined
  }

  const range = voiceName
    ? getVoiceSpeedRange(voiceName)
    : { min: LIMITED_VOICE_SPEED_MIN, max: LIMITED_VOICE_SPEED_MAX }
  if (!range) {
    return undefined
  }

  return Math.min(range.max, Math.max(range.min, value))
}

export function voiceSupportsSpeed(voiceName?: string): boolean {
  return getVoiceSpeedRange(voiceName) !== undefined
}
